import { Injectable } from "@angular/core";
import { ObgConfigFeAdaptiveClientCore } from "@obg/core.config";
import { ConfigService, fromSecondsToMilliseconds } from "@obg/ngx.global";

import { DEFAULT_BONUS_CONTEXT, DEFAULT_TRANSACTION_BONUS_CONTEXTS } from "./wallet.const";
import { BonusContext, TransactionBonusContext } from "./wallet.model";

@Injectable({
	providedIn: "root",
})
export class WalletConfigService {
	constructor(private configService: ConfigService<ObgConfigFeAdaptiveClientCore>) {}

	getWalletUpdateTopic(): string {
		return this.configService.get((x) => x.core.wallet.walletRealtimeTopic, "wallet-balance-updated");
	}

	isRealtimeBalanceEnabled(): boolean {
		return this.configService.get((x) => x.core.wallet.isRealtimeBalanceEnabled, false);
	}

	getBalanceEndpoint(): string {
		return this.configService.get((x) => x.core.wallet.endpoints!.balance, "v2/wallet/balance/");
	}

	getBalanceContextEndpoint(): string {
		return this.configService.get((x) => x.core.wallet.endpoints!.balanceContext, "v2/wallet/balance/:context/");
	}

	getBetWinSummaryEndpoint(): string {
		return this.configService.get((x) => x.core.wallet.endpoints!.betWinSummary, "v1/wallet/betwinsummary/");
	}

	getTransactionsEndpoint(): string {
		return this.configService.get((x) => x.core.wallet.endpoints?.transactions, "v1/wallet/transactions/");
	}

	getTransactionEndpoint(): string {
		return this.configService.get(
			(x) => x.core.wallet.endpoints!.transaction,
			"v1/wallet/transactions/:externalTransactionId/"
		);
	}

	getTransactionTotalsEndpoint(): string {
		return this.configService.get(
			(x) => x.core.wallet.endpoints!.transactionTotals,
			"v2/wallet/transactiontotals/"
		);
	}

	getWithdrawalRestrictionEndpoint(): string {
		return this.configService.get(
			(x) => x.core.wallet.endpoints!.withdrawalRestriction,
			"v1/co/wallet/withdrawalrestrictions/"
		);
	}

	getBonusBalanceByGameIdsEndpoint(): string {
		return this.configService.get((x) => x.core.wallet.endpoints!.gameBalance, "v1/wallet/balance/games");
	}

	getPolledBalanceInterval(): number {
		return this.configService.get((x) => x.core.wallet.realTimeBalanceInterval, fromSecondsToMilliseconds(30));
	}

	getTransactionBonusContexts(): TransactionBonusContext[] {
		return this.configService.get(
			(x) => x.core.wallet.bonusContexts,
			DEFAULT_TRANSACTION_BONUS_CONTEXTS
		) as TransactionBonusContext[];
	}

	getBalanceByProductEnabled(): boolean {
		return this.configService.get((x) => x.core.wallet.balanceByProductEnabled, false);
	}

	getBonusContextByProduct(): Record<string, BonusContext> {
		return this.configService.get((x) => x.core.wallet.bonusContextByProduct, DEFAULT_BONUS_CONTEXT) as Record<
			string,
			BonusContext
		>;
	}

	getTransactionTotalsEnabled(): boolean {
		return this.configService.get((x) => x.core.wallet.transactionTotalsEnabled, false);
	}

	getBonusTotalsEnabled(): boolean {
		return this.configService.get((x) => x.core.wallet.bonusTotalsEnabled, false);
	}

	getBalanceEnabled(): boolean {
		return this.configService.get((x) => x.core.wallet.balanceEnabled, true);
	}

	isByPassTransactionsDownloadRequirementsEnabled(): boolean {
		return this.configService.get((x) => x.core.wallet.isByPassTransactionsDownloadRequirementsEnabled, false);
	}
}
